/**
 * Credentials never belong in diagnostics. Callers should log safe facts;
 * this boundary also handles older cached entries and credential-bearing errors.
 * It is deliberately separate from operational invitation/authentication state.
 */
const REDACTED = '[REDACTED]';
const OMITTED = '[omitted: diagnostic limit]';
const MAX_STRING_LENGTH = 8000;
const MAX_DEPTH = 8;
const MAX_FIELDS = 100;
const MAX_VALUES = 2000;
const isCredentialKey = (key) => /token|password|passwd|secret|authorization|cookie|credential|privatekey|apikey|signature|jwt/i
    .test(key.replace(/[^a-z0-9]/gi, ''));
export function redactDiagnosticText(value) {
    // Do not truncate a credential into an unrecognizable but still sensitive fragment.
    if (value.length > MAX_STRING_LENGTH) {
        return OMITTED;
    }
    // Decode bounded layers before redacting: native errors sometimes encode an
    // already-encoded link. Remaining encoded text is omitted, not partly exposed.
    let decoded = value;
    for (let attempt = 0; attempt < 3 && /%[a-f0-9]{2}/i.test(decoded); attempt++) {
        decoded = decoded.replace(/(?:%[a-f0-9]{2})+/gi, encoded => {
            try {
                return decodeURIComponent(encoded);
            }
            catch {
                return REDACTED;
            }
        });
    }
    if (/%[a-f0-9]{2}/i.test(decoded)) {
        return OMITTED;
    }
    // Serialized error bodies may contain credential arrays or nested objects.
    if (/^\s*[\[{]/.test(decoded)) {
        try {
            return JSON.stringify(redactDiagnosticValue(JSON.parse(decoded)));
        }
        catch {
            // Non-JSON error text still receives the field/link redaction below.
        }
    }
    return decoded
        .replace(/([a-z][a-z0-9+.-]*:\/\/)[^\s/?#@]+@/gi, '$1[REDACTED]@')
        .replace(/(\/j\/)[^\s/?#"'<>\\)]+/gi, '$1[REDACTED]')
        .replace(/\b(Bearer|Basic)\s+[a-z0-9._~+/-]+=*/gi, '$1 [REDACTED]')
        .replace(/\beyJ[a-z0-9_-]*\.[a-z0-9_-]+\.[a-z0-9_-]*/gi, REDACTED)
        .replace(/(["']?)(\b[\w%-]*(?:token|password|passwd|secret|authorization|cookie|credential|private[-_]?key|api[-_]?key|signature|jwt)[\w%-]*)\1(\s*[:=]\s*)(?:\[REDACTED\]|"[^"]*"|'[^']*'|[^\s&#,;)}\]]+)/gi, (_match, quote, key, separator) => quote + key + quote + separator + REDACTED);
}
export function redactDiagnosticValue(input) {
    const seen = new WeakSet();
    let remaining = MAX_VALUES;
    const visit = (value, depth) => {
        if (--remaining < 0 || depth > MAX_DEPTH) {
            return OMITTED;
        }
        if (typeof value === 'string') {
            return redactDiagnosticText(value);
        }
        if (value === null || value === undefined || typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }
        if (typeof value !== 'object') {
            return '[omitted: unsupported diagnostic]';
        }
        if (seen.has(value)) {
            return '[omitted: circular diagnostic]';
        }
        seen.add(value);
        try {
            if (Array.isArray(value)) {
                return value.slice(0, MAX_FIELDS).map(item => visit(item, depth + 1));
            }
            const entries = value instanceof Error
                ? Object.entries({ name: value.name, message: value.message, stack: value.stack })
                : Object.entries(value);
            return Object.fromEntries(entries.slice(0, MAX_FIELDS).map(([key, item]) => [
                redactDiagnosticText(key),
                isCredentialKey(redactDiagnosticText(key)) ? REDACTED : visit(item, depth + 1)
            ]));
        }
        catch {
            return '[omitted: unreadable diagnostic]';
        }
        finally {
            seen.delete(value);
        }
    };
    return visit(input, 0);
}
export function redactDiagnosticRecord(value) {
    const sanitized = redactDiagnosticValue(value);
    return sanitized !== null && typeof sanitized === 'object' && !Array.isArray(sanitized)
        ? sanitized
        : { diagnostic: sanitized };
}
